import React, { useState, useEffect } from "react";
import Card from "../../user/components/Card";
import { useTranslation } from "react-i18next";
import { getDashboardData } from "../services/dashboard.service";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/commonfunction";
import { ROLE, SWAL_SETTINGS } from "../../../utils/Constants";
import Swal from "sweetalert2";

function Dashbaord() {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser(ROLE.ADVERTISER);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return t("dashboard.greeting.morning");
    if (hour >= 12 && hour < 17) return t("dashboard.greeting.afternoon");
    if (hour >= 17 && hour < 21) return t("dashboard.greeting.evening");
    return t("dashboard.greeting.night");
  };

  const fetchDashboard = async (searchValue = "") => {
    try {
      setLoading(true);
      const response = await getDashboardData(searchValue);

      if (response.success) {
        setDashboardData(response.data);
        setError(null);
      } else {
        setError(response.message || "Failed to fetch dashboard data");
      }
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchDashboard();
    
    if (location.state?.loginSuccessful) {
      Swal.fire({
        icon: "success",
        text: location.state.message || "Successfully logged in",
        ...SWAL_SETTINGS,
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);
  const handleSearch = () => {
    fetchDashboard(search);
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDashboard(search);
    }, 1000); // debounce delay

    return () => clearTimeout(timer);
  }, [search]);

  const [selectedPlatform, setSelectedPlatform] = useState("Local");

  const statsData = [
    {
      title: t("dashboard.stats.live_streams"),
      value: dashboardData?.liveStreams || 0,
      color: "bg-red-400",
      icon: "/images/d-icon1.svg",
    },
    {
      title: t("dashboard.stats.listed_products"),
      value: dashboardData?.totalProducts || 0,
      color: "bg-secondary-400",
      icon: "/images/d-icon2.svg",
    },
    {
      title: t("dashboard.stats.total_leads"),
      value: dashboardData?.totalLeads || 0,
      color: "bg-orange-400",
      icon: "/images/d-icon3.svg",
    },
    {
      title: t("dashboard.stats.purchase_requests"),
      value: dashboardData?.totalPurchaseRequests || 0,
      color: "bg-green-500",
      icon: "/images/d-icon3.svg",
    },
    // {
    //   title: t("dashboard.stats.product_views"),
    //   value: dashboardData?.totalProductViews || 0,
    //   color: "bg-teal-500",
    //   icon: "/images/d-icon2.svg",
    // },
    // {
    //   title: t("dashboard.stats.buy_now_clicks"),
    //   value: dashboardData?.totalBuyNowClicks || 0,
    //   color: "bg-indigo-500",
    //   icon: "/images/search1.svg",
    // },
    {
      title: t("dashboard.stats.whatsapp_leads"),
      value: dashboardData?.totalWhatsappRedirects || 0,
      color: "bg-green-600",
      icon: "/images/d-icon3.svg",
    },
    // {
    //   title: t("dashboard.stats.global_reach"),
    //   value: dashboardData?.totalGlobalReach || 0,
    //   color: "bg-sky-500",
    //   icon: "/images/d-icon4.svg",
    // },
    {
      title: t("dashboard.stats.total_likes_all"),
      value: dashboardData?.totalStreamsViews?.totalLikes || 0,
      color: "bg-rose-500",
      icon: "/images/d-icon1.svg",
    },
  ];

  const platforms = [
    { name: "Local", key: "Local" },
    { name: "Facebook", key: "Facebook" },
    { name: "YouTube", key: "Youtube" },
    { name: "Twitch", key: "Twitch" },
  ];

  const getPlatformStats = (platformKey) => {
    const viewsKey = `total${platformKey}Views`;
    const likesKey = `total${platformKey}Likes`;
    return {
      views: dashboardData?.totalStreamsViews?.[viewsKey] || 0,
      likes: dashboardData?.totalStreamsViews?.[likesKey] || 0,
    };
  };

  const currentPlatformStats = getPlatformStats(selectedPlatform);

  const recentStreaming = dashboardData?.recentPastStream?.map(stream => ({
    images: stream.thumbnail || "/images/card1.png",
    _id: stream._id,
    title: stream.title || t("dashboard.untitled_stream"),
    subtitle: stream.description || t("dashboard.no_description"),
    time: false,
  })) || [
      {
        images: "/images/card1.png",
        title: "Fashion Collection",
        subtitle: "Sarah’s Boutique",
        time: false,
      },
      {
        images: "/images/card2.png",
        title: "Fashion Collection",
        subtitle: "Sarah’s Boutique",
        time: false,
      },
      {
        images: "/images/card3.png",
        title: "Fashion Collection",
        subtitle: "Sarah’s Boutique",
        time: false,
      },
      {
        images: "/images/card4.png",
        title: "Fashion Collection",
        subtitle: "Sarah’s Boutique",
        time: false,
      },
    ];

  // Removed the early return for `loading` to prevent severe layout shift.
  // The layout will render immediately with default values (0), and update seamlessly when data arrives.

  if (error) {
    return (
      <div className="dashbaord-bg py-10">
        <div className="container">
          <div className="text-center py-10">
            <p className="body-lg text-red-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashbaord-bg py-10">
        <div className="container">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-zinc-800">
              {getGreeting()}, {user?.name || t("header.role_advertiser")}!
            </h2>
            <p className="text-zinc-500 body-sm mt-1">{t("dashboard.account_summary")}</p>
          </div>
          <h1 className="headings-h1 text-zinc-950 border-b border-gray-200 pb-[15px] mb-[30px]">
            {t("dashboard.title")}
          </h1>


          <div className="flex items-center justify-between flex-wrap gap-[15px] mb-4">
            <h2 className="headings-h2 text-zinc-950">{t("dashboard.streaming_overview")}</h2>
            <div className="search-box relative flex-1 max-w-[425px]">
              <input
                type="text"
                className="form-control-custom !pr-[50px] min-w-[250px] md:min-w-[425px]"
                placeholder={t("common.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button className="absolute right-2 top-2" onClick={handleSearch}>
                <img src="/images/search1.svg" alt="search" />
              </button>
            </div>
          </div>
        </div>

        <section className="py-12 pt-0">
          <div className="container">
            <div className="stats-data-list grid grid-cols-2 lg:grid-cols-4 gap-[15px] mb-12">
              {statsData.map((item, index) => (
                <div
                  key={index}
                  className={`stats-data-item flex items-center p-[15px] md:p-[30px] rounded-[10px] gap-[15px] ${item.color}`}
                >
                  <div className="stats-data-content flex flex-col gap-[10px]">
                    <h3 className="text-[18px] !leading-[22px] text-white">
                      {item.title}
                    </h3>
                    <h2 className="heading-web-h2 text-white">{item.value}</h2>
                  </div>
                  <div className="stats-data-icon">
                    <img src={item.icon} alt={item.title} />
                  </div>
                </div>
              ))}
            </div>

            {/* Platform Insights Section */}
            <div className="platform-insights bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h2 className="headings-h2 text-zinc-950 mb-6">{t("dashboard.platform_insights")}</h2>
              
              <div className="flex bg-gray-50 p-1 rounded-lg w-fit mb-8 overflow-x-auto no-scrollbar max-w-full">
                {platforms.map((platform) => (
                  <button
                    key={platform.key}
                    onClick={() => setSelectedPlatform(platform.key)}
                    className={`px-6 py-2 rounded-md font-medium transition-all duration-200 whitespace-nowrap ${
                      selectedPlatform === platform.key
                        ? "bg-red-400 text-white shadow-sm"
                        : "text-zinc-500 hover:text-zinc-700"
                    }`}
                  >
                    {platform.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-6 p-6 bg-gray-50 rounded-xl transition-all hover:bg-gray-100">
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center border border-gray-200 shadow-sm transition-transform hover:scale-110">
                    <img src="/images/eye01.svg" alt="Views" className="w-8 h-8 opacity-80" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm font-medium uppercase tracking-wider mb-1">{t("dashboard.views")}</p>
                    <h3 className="heading-web-h2 text-zinc-950">{currentPlatformStats.views}</h3>
                  </div>
                </div>

                <div className="flex items-center gap-6 p-6 bg-gray-50 rounded-xl transition-all hover:bg-gray-100">
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center border border-gray-200 shadow-sm transition-transform hover:scale-110">
                    <img src="/images/heart01.svg" alt="Likes" className="w-8 h-8 opacity-80" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm font-medium uppercase tracking-wider mb-1">{t("dashboard.likes")}</p>
                    <h3 className="heading-web-h2 text-zinc-950">{currentPlatformStats.likes}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="past-streaming-section bg-white">
          <div className="container">
            <div className="section-head flex justify-between items-center gap-[10px] mb-8 flex-wrap">
              <h2 className="heading-web-h2 text-[var(--black-scale-900)]">
                {t("dashboard.recent_streaming")}
              </h2>
              <a onClick={() => navigate("/advertiser/streams")} className="link">{t("dashboard.btn_view_all")}</a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px]">
              {recentStreaming.map((item, index) => (
                <div key={index} onClick={() => navigate(`/advertiser/streams/stream-details/${item?._id}`)}>
              
                <Card
                  key={index}
                  images={item.images}
                  title={item.title}
                  subtitle={item.subtitle}
                  time={item.time}
                  live={item.live}
                />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Dashbaord;
